import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";

// Ideal Postcodes - Paid UK postcode API with full address lookup
const IDEAL_POSTCODES_API_KEY = "ak_mlehyt6uH8cPFLODV8dTvr6HCbuhX";

export const PostcodeLookup = ({ onAddressSelect, className = "" }) => {
  const [postcode, setPostcode] = useState("");
  const [loading, setLoading] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState("");

  const lookupPostcode = async () => {
    if (!postcode.trim()) {
      toast.error("Please enter a postcode");
      return;
    }

    setLoading(true);
    setAddresses([]);
    setSelectedAddress("");

    try {
      // Format postcode - Ideal Postcodes accepts with or without space
      const cleanPostcode = postcode.replace(/\s/g, "").toUpperCase();
      const formattedPostcode = encodeURIComponent(cleanPostcode);
      
      const response = await fetch(
        `https://api.ideal-postcodes.co.uk/v1/postcodes/${formattedPostcode}?api_key=${IDEAL_POSTCODES_API_KEY}`
      );

      const data = await response.json();

      if (data.code === 2000 && data.result && data.result.length > 0) {
        setAddresses(data.result);
        toast.success(`Found ${data.result.length} addresses`);
      } else if (data.code === 4040) {
        toast.error("Postcode not found");
      } else if (data.code === 4010 || data.code === 4012) {
        toast.error("Invalid API key or limit reached");
      } else {
        toast.error(data.message || "No addresses found for this postcode");
      }
    } catch (error) {
      console.error("Postcode lookup error:", error);
      toast.error("Failed to lookup postcode");
    } finally {
      setLoading(false);
    }
  };

  const handleAddressSelect = (index) => {
    const addr = addresses[index];
    if (!addr) return;

    setSelectedAddress(index.toString());

    // Map Ideal Postcodes fields to our standard format
    const addressData = {
      line_1: addr.line_1 || "",
      line_2: addr.line_2 || "",
      line_3: addr.line_3 || "",
      town: addr.post_town || "",
      county: addr.county || "",
      postcode: addr.postcode || "",
      country: addr.country || "England",
      // Additional useful fields
      building_name: addr.building_name || "",
      building_number: addr.building_number || "",
      thoroughfare: addr.thoroughfare || "",
      locality: addr.dependent_locality || "",
      uprn: addr.uprn || "",
    };

    onAddressSelect(addressData);
  };

  const formatAddressForDisplay = (addr) => {
    const parts = [];
    if (addr.line_1) parts.push(addr.line_1);
    if (addr.line_2) parts.push(addr.line_2);
    if (addr.post_town) parts.push(addr.post_town);
    if (addr.postcode) parts.push(addr.postcode);
    return parts.join(", ");
  };

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="flex gap-2">
        <div className="flex-1">
          <Label className="text-xs text-slate-500 mb-1 block">Postcode Lookup</Label>
          <div className="flex gap-2">
            <Input
              placeholder="Enter postcode (e.g. MK40 1AA)"
              value={postcode}
              onChange={(e) => setPostcode(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && lookupPostcode()}
              className="flex-1"
              data-testid="postcode-lookup-input"
            />
            <Button
              type="button"
              onClick={lookupPostcode}
              disabled={loading}
              data-testid="postcode-lookup-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>
      </div>

      {addresses.length > 0 && (
        <div>
          <Label className="text-xs text-slate-500 mb-1 block flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            Select Address ({addresses.length} found)
          </Label>
          <Select value={selectedAddress} onValueChange={(v) => handleAddressSelect(parseInt(v))}>
            <SelectTrigger data-testid="address-select">
              <SelectValue placeholder="Choose an address..." />
            </SelectTrigger>
            <SelectContent className="max-h-[300px] z-[9999]" position="popper" sideOffset={5}>
              {addresses.map((addr, idx) => (
                <SelectItem key={idx} value={idx.toString()}>
                  {formatAddressForDisplay(addr)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
};

export default PostcodeLookup;
